import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { BookMarked, Loader2, ExternalLink, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

export default function EvidenceDialog({ open, onOpenChange, userId, projectId, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Reset query if a new initialQuery comes in (when re-opened with new paragraph)
  if (open && initialQuery && initialQuery !== query && results.length === 0) {
    setQuery(initialQuery);
  }

  const runRetrieve = async (qOverride) => {
    const q = (qOverride ?? query).trim();
    if (!q) { toast.error('Въведи текст за търсене'); return; }
    setLoading(true);
    try {
      const data = await thesisApi.retrieveEvidence(userId, projectId, q, 6);
      setResults(data.results || []);
      if (!data.results?.length) toast.info('Няма намерени източници');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retrieval failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setResults([]); } }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="evidence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            <BookMarked className="w-5 h-5 text-teal-700" />
            Evidence Provenance Viewer
          </DialogTitle>
          <DialogDescription>
            Покажи кои източници от проектната библиотека биха обосновали даден текст.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">Параграф / твърдение</label>
            <Textarea
              data-testid="evidence-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Постави текст от глава или твърдение което искаш да провериш…"
              rows={3}
              className="resize-none"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={() => runRetrieve()} disabled={loading || !query.trim()} data-testid="evidence-run-btn" className="bg-teal-700 hover:bg-teal-800">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Търся…</> : 'Покажи източници'}
            </Button>
          </div>

          {results.length > 0 && (
            <div className="space-y-3 pt-2" data-testid="evidence-results">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Намерени {results.length} източника
              </div>
              {results.map((r, i) => {
                const sim = typeof r.similarity === 'number' ? Math.round(r.similarity * 100) : null;
                return (
                  <Card key={r.doc_id ? `${r.doc_id}-${i}` : `result-${i}`} className="p-3 border-teal-200/40 dark:border-teal-900/40">
                    <div className="flex items-start gap-2 mb-2 flex-wrap">
                      <Badge variant="outline" className="font-mono text-[10px]">#{i + 1}</Badge>
                      {sim !== null && (
                        <Badge variant="secondary" className="text-[10px] bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-400">
                          {sim}% match
                        </Badge>
                      )}
                      {r.source_type && r.source_type !== 'upload' && (
                        <Badge variant="outline" className="text-[10px]">{r.source_type}</Badge>
                      )}
                      {r.page && <Badge variant="outline" className="text-[10px]">p. {r.page}</Badge>}
                    </div>
                    {r.source_title && (
                      <div className="text-sm font-semibold mb-1 flex items-start gap-1.5">
                        <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                        <span className="leading-snug">{r.source_title}</span>
                      </div>
                    )}
                    <div className="flex gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
                      {r.doi && (
                        <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noreferrer" className="text-teal-700 dark:text-teal-400 hover:underline inline-flex items-center gap-0.5">
                          DOI: {r.doi} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {r.pmid && (
                        <a href={`https://pubmed.ncbi.nlm.nih.gov/${r.pmid}/`} target="_blank" rel="noreferrer" className="text-teal-700 dark:text-teal-400 hover:underline inline-flex items-center gap-0.5">
                          PMID: {r.pmid} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    <blockquote className="text-sm leading-relaxed text-foreground/85 border-l-2 border-teal-600/60 pl-3 italic">
                      "{r.text}"
                    </blockquote>
                  </Card>
                );
              })}
            </div>
          )}

          {loading && (
            <div className="text-center py-6 text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
              Семантично търсене в проектната библиотека…
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
